<template>
    <div :dir="$root.$i18n.locale == 'ar' ? 'rtl' : 'ltr'" class="tw-min-h-screen tw-max-h-screen tw-bg-gradient-to-br tw-from-primary-50 tw-to-white tw-overflow-hidden">
        <div class="tw-grid tw-grid-cols-12 tw-h-screen">
            <!-- Left Side - Background Image with Enhanced Content -->
            <div class="tw-col-span-12 lg:tw-col-span-7 tw-relative tw-hidden lg:tw-block">
                <!-- Background Image -->
                <img :src="$frontend(brand.images.onboardingBg)" class="tw-h-full tw-w-full tw-object-cover">

                <!-- Enhanced Gradient Overlay -->
                <div class="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-gradient-to-br tw-from-black/90 tw-via-primary-900/80 tw-to-primary-600/60 tw-flex tw-flex-col tw-justify-center tw-items-center tw-p-8 lg:tw-p-12">
                    <!-- Logo with white filter -->
                    <div class="tw-mb-8 tw-transform tw-scale-110">
                        <div class="tw-filter tw-brightness-0 tw-invert">
                            <AppLogo />
                        </div>
                    </div>

                    <!-- Enhanced Welcome Content -->
                    <div class="tw-text-center tw-text-white tw-max-w-xl">
                        <!-- Main Heading -->
                        <h1 class="tw-text-5xl lg:tw-text-6xl tw-font-bold tw-mb-6 tw-leading-tight tw-drop-shadow-2xl tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3">
                            <span>{{ $t('onboarding.welcome.title') }} </span>
                            <span class="tw-text-transparent tw-bg-clip-text tw-bg-gradient-to-r tw-from-primary-200 tw-to-primary-300">
                                {{ $t('onboarding.welcome.brand') }}
                            </span>
                        </h1>

                        <!-- Subtitle -->
                        <p class="tw-text-xl lg:tw-text-2xl tw-font-medium tw-opacity-95 tw-leading-relaxed tw-drop-shadow-lg tw-mb-8">
                            {{ $t('onboarding.welcome.subtitle') }}
                        </p>

                        <!-- Progress Info -->
                        <div class="tw-bg-white/10 tw-backdrop-blur-sm tw-rounded-2xl tw-p-6 tw-mb-8 tw-border tw-border-white/20">
                            <!-- <div class="tw-flex tw-items-center tw-justify-center tw-gap-4 tw-mb-4">
                                <icon icon="solar:users-group-two-rounded-bold-duotone" class="tw-text-white tw-text-2xl" />
                                <span class="tw-text-lg tw-font-medium">{{ $t('onboarding.welcome.community_title') }}</span>
                            </div> -->
                            <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 tw-text-sm">
                                <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-text-center">
                                    <div class="tw-w-8 tw-h-8 tw-bg-white/20 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                                        <icon icon="solar:share-circle-bold-duotone" class="tw-text-white tw-text-xl" />
                                    </div>
                                    <span class="tw-font-medium">{{ $t('onboarding.welcome.connect_title') }}</span>
                                    <span class="tw-text-xs tw-opacity-90">{{ $t('onboarding.welcome.connect_subtitle') }}</span>
                                </div>
                                <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-text-center">
                                    <div class="tw-w-8 tw-h-8 tw-bg-white/20 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                                        <icon icon="solar:diploma-bold-duotone" class="tw-text-white tw-text-xl" />
                                    </div>
                                    <span class="tw-font-medium">{{ $t('onboarding.welcome.showcase_title') }}</span>
                                    <span class="tw-text-xs tw-opacity-90">{{ $t('onboarding.welcome.showcase_subtitle') }}</span>
                                </div>
                                <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-text-center">
                                    <div class="tw-w-8 tw-h-8 tw-bg-white/20 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                                        <icon icon="solar:rocket-bold-duotone" class="tw-text-white tw-text-xl" />
                                    </div>
                                    <span class="tw-font-medium">{{ $t('onboarding.welcome.grow_title') }}</span>
                                    <span class="tw-text-xs tw-opacity-90">{{ $t('onboarding.welcome.grow_subtitle') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Decorative Elements -->
                    <div class="tw-absolute tw-top-10 tw-right-10 tw-opacity-20">
                        <icon icon="solar:star-bold-duotone" class="tw-text-white tw-text-6xl tw-animate-pulse" />
                    </div>
                    <div class="tw-absolute tw-bottom-10 tw-left-10 tw-opacity-20">
                        <icon icon="solar:heart-bold-duotone" class="tw-text-white tw-text-5xl tw-animate-pulse" style="animation-delay: 1s;" />
                    </div>
                </div>
            </div>

            <!-- Mobile Header (shown only on mobile) -->
            <div class="tw-col-span-12 lg:tw-hidden tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-700 tw-p-4">
                <div class="tw-text-center tw-text-white">
                    <div class="tw-filter tw-brightness-0 tw-invert tw-mb-3 tw-transform tw-scale-90">
                        <AppLogo />
                    </div>
                    <h1 class="tw-text-xl tw-font-bold tw-mb-2">
                        {{ $t('onboarding.welcome.mobile_title') }}
                        <span class="tw-text-yellow-300">🎉</span>
                    </h1>

                    <!-- Mobile Navigation Dots -->
                    <div class="tw-flex tw-justify-center tw-gap-2 tw-mt-3">
                        <button
                            v-for="(slide, index) in totalSlides"
                            :key="index"
                            @click="goToSlide(index)"
                            :class="[
                                'tw-w-2 tw-h-2 tw-rounded-full tw-transition-all tw-duration-300',
                                slideHasErrors[index]
                                    ? 'tw-bg-red-400 tw-w-4 tw-animate-pulse'
                                    : currentSlide === index
                                        ? 'tw-bg-white tw-w-6'
                                        : 'tw-bg-white/60 hover:tw-bg-white/80'
                            ]"
                        ></button>
                    </div>
                </div>
            </div>

            <!-- Right Side - Form Content (Narrower) -->
            <div class="tw-col-span-12 lg:tw-col-span-5 tw-bg-white tw-shadow-xl lg:tw-shadow-2xl tw-flex tw-flex-col tw-h-full lg:tw-h-screen tw-max-h-screen">

                <!-- Loading Skeleton -->
                <div v-if="loading" class="tw-flex-1 tw-flex tw-flex-col tw-p-3 sm:tw-p-4 lg:tw-p-5 tw-overflow-hidden tw-animate-pulse">
                    <!-- Step Indicator Skeleton -->
                    <div class="tw-flex tw-items-center tw-justify-center tw-mb-6 tw-flex-shrink-0">
                        <div class="tw-flex tw-items-center tw-gap-1 sm:tw-gap-2">
                            <!-- Step 1 Skeleton -->
                            <div class="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                <div class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-200"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-16 tw-hidden sm:tw-block"></div>
                            </div>
                            <!-- Line 1 Skeleton -->
                            <div class="tw-w-6 sm:tw-w-8 tw-h-0.5 tw-bg-gray-200"></div>
                            <!-- Step 2 Skeleton -->
                            <div class="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                <div class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-200"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-20 tw-hidden sm:tw-block"></div>
                            </div>
                            <!-- Line 2 Skeleton -->
                            <div class="tw-w-6 sm:tw-w-8 tw-h-0.5 tw-bg-gray-200"></div>
                            <!-- Step 3 Skeleton -->
                            <div class="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                <div class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-200"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-16 tw-hidden sm:tw-block"></div>
                            </div>
                            <!-- Line 3 Skeleton -->
                            <div class="tw-w-6 sm:tw-w-8 tw-h-0.5 tw-bg-gray-200"></div>
                            <!-- Step 4 Skeleton -->
                            <div class="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                <div class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-200"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-14 tw-hidden sm:tw-block"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Form Content Skeleton -->
                    <div class="tw-flex-1 tw-relative tw-overflow-y-auto tw-overflow-x-hidden tw-space-y-6">
                        <!-- Title Skeleton -->
                        <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-3/4 tw-mx-auto"></div>

                        <!-- Form Fields Skeleton -->
                        <div class="tw-space-y-4">
                            <!-- Field 1 -->
                            <div>
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-mb-2"></div>
                                <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-full"></div>
                            </div>
                            <!-- Field 2 -->
                            <div>
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-2"></div>
                                <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-full"></div>
                            </div>
                            <!-- Field 3 -->
                            <div>
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-28 tw-mb-2"></div>
                                <div class="tw-h-24 tw-bg-gray-200 tw-rounded tw-w-full"></div>
                            </div>
                            <!-- Field 4 -->
                            <div>
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-20 tw-mb-2"></div>
                                <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-full"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Navigation Buttons Skeleton -->
                    <div class="tw-flex tw-items-center tw-justify-between tw-pt-3 sm:tw-pt-4 tw-border-t tw-border-gray-200 tw-mt-3 sm:tw-mt-4 tw-flex-shrink-0">
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-24"></div>
                        <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-32"></div>
                    </div>

                    <!-- Skip Option Skeleton -->
                    <div class="tw-text-center tw-mt-2 tw-flex-shrink-0">
                        <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-20 tw-mx-auto"></div>
                    </div>
                </div>

                <!-- Actual Form Container -->
                <div v-else class="tw-flex-1 tw-flex tw-flex-col tw-p-3 sm:tw-p-4 lg:tw-p-5 tw-overflow-hidden">
                    <!-- Modern Step Indicator -->
                    <div class="tw-flex tw-items-center tw-justify-center tw-mb-6 tw-flex-shrink-0">
                        <div class="tw-flex tw-items-center tw-gap-1 sm:tw-gap-2">
                            <!-- Step 1 - Language -->
                            <div class="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                <button
                                    @click="goToSlide(0)"
                                    :class="[
                                        'tw-w-8 tw-h-8 tw-rounded-full tw-transition-all tw-duration-300 tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-bold tw-border-2',
                                        slideHasErrors[0]
                                            ? 'tw-bg-red-500 tw-border-red-500 tw-text-white tw-shadow-lg tw-animate-pulse'
                                            : currentSlide >= 0
                                                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white tw-shadow-lg'
                                                : 'tw-bg-white tw-border-gray-300 tw-text-gray-400'
                                    ]"
                                >
                                    <icon v-if="slideHasErrors[0]" icon="solar:danger-triangle-bold" class="tw-text-white tw-text-sm" />
                                    <icon v-else-if="currentSlide > 0" icon="solar:check-circle-bold" class="tw-text-white tw-text-sm" />
                                    <icon v-else icon="solar:global-bold-duotone" class="tw-text-white tw-text-sm" />
                                </button>
                                <span :class="[
                                    'tw-text-xs tw-font-medium tw-text-center tw-hidden sm:tw-block',
                                    slideHasErrors[0]
                                        ? 'tw-text-red-500'
                                        : currentSlide >= 0 ? 'tw-text-primary-600' : 'tw-text-gray-400'
                                ]">{{ $t('onboarding.progress.language') }}</span>
                            </div>

                            <!-- Connecting Line 1 -->
                            <div :class="[
                                'tw-w-6 sm:tw-w-8 tw-h-0.5 tw-transition-all tw-duration-500',
                                currentSlide > 0 ? 'tw-bg-primary-600' : 'tw-bg-gray-300'
                            ]"></div>

                            <!-- Step 2 - Basic Info -->
                            <div class="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                <button
                                    @click="goToSlide(1)"
                                    :class="[
                                        'tw-w-8 tw-h-8 tw-rounded-full tw-transition-all tw-duration-300 tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-bold tw-border-2',
                                        slideHasErrors[1]
                                            ? 'tw-bg-red-500 tw-border-red-500 tw-text-white tw-shadow-lg tw-animate-pulse'
                                            : currentSlide >= 1
                                                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white tw-shadow-lg'
                                                : currentSlide === 0
                                                    ? 'tw-bg-white tw-border-primary-600 tw-text-primary-600'
                                                    : 'tw-bg-white tw-border-gray-300 tw-text-gray-400'
                                    ]"
                                >
                                    <icon v-if="slideHasErrors[1]" icon="solar:danger-triangle-bold" class="tw-text-white tw-text-sm" />
                                    <icon v-else-if="currentSlide > 1" icon="solar:check-circle-bold" class="tw-text-white tw-text-sm" />
                                    <span v-else>1</span>
                                </button>
                                <span :class="[
                                    'tw-text-xs tw-font-medium tw-text-center tw-hidden sm:tw-block',
                                    slideHasErrors[1]
                                        ? 'tw-text-red-500'
                                        : currentSlide >= 1 ? 'tw-text-primary-600' : currentSlide === 0 ? 'tw-text-primary-600' : 'tw-text-gray-400'
                                ]">{{ $t('onboarding.progress.basic_info') }}</span>
                            </div>

                            <!-- Connecting Line 2 -->
                            <div :class="[
                                'tw-w-6 sm:tw-w-8 tw-h-0.5 tw-transition-all tw-duration-500',
                                currentSlide > 1 ? 'tw-bg-primary-600' : 'tw-bg-gray-300'
                            ]"></div>

                            <!-- Step 3 - About & Address -->
                            <div class="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                <button
                                    @click="goToSlide(2)"
                                    :class="[
                                        'tw-w-8 tw-h-8 tw-rounded-full tw-transition-all tw-duration-300 tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-bold tw-border-2',
                                        slideHasErrors[2]
                                            ? 'tw-bg-red-500 tw-border-red-500 tw-text-white tw-shadow-lg tw-animate-pulse'
                                            : currentSlide >= 2
                                                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white tw-shadow-lg'
                                                : currentSlide >= 1
                                                    ? 'tw-bg-white tw-border-primary-600 tw-text-primary-600'
                                                    : 'tw-bg-white tw-border-gray-300 tw-text-gray-400'
                                    ]"
                                >
                                    <icon v-if="slideHasErrors[2]" icon="solar:danger-triangle-bold" class="tw-text-white tw-text-sm" />
                                    <icon v-else-if="currentSlide > 2" icon="solar:check-circle-bold" class="tw-text-white tw-text-sm" />
                                    <span v-else>2</span>
                                </button>
                                <span :class="[
                                    'tw-text-xs tw-font-medium tw-text-center tw-hidden sm:tw-block',
                                    slideHasErrors[2]
                                        ? 'tw-text-red-500'
                                        : currentSlide >= 2 ? 'tw-text-primary-600' : currentSlide >= 1 ? 'tw-text-primary-600' : 'tw-text-gray-400'
                                ]">{{ $t('onboarding.progress.about') }}</span>
                            </div>

                            <!-- Connecting Line 3 -->
                            <div :class="[
                                'tw-w-6 sm:tw-w-8 tw-h-0.5 tw-transition-all tw-duration-500',
                                currentSlide > 2 ? 'tw-bg-primary-600' : 'tw-bg-gray-300'
                            ]"></div>

                            <!-- Step 4 - Social Media -->
                            <div class="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                <button
                                    @click="goToSlide(3)"
                                    :class="[
                                        'tw-w-8 tw-h-8 tw-rounded-full tw-transition-all tw-duration-300 tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-bold tw-border-2',
                                        slideHasErrors[3]
                                            ? 'tw-bg-red-500 tw-border-red-500 tw-text-white tw-shadow-lg tw-animate-pulse'
                                            : currentSlide >= 3
                                                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white tw-shadow-lg'
                                                : currentSlide >= 2
                                                    ? 'tw-bg-white tw-border-primary-600 tw-text-primary-600'
                                                    : 'tw-bg-white tw-border-gray-300 tw-text-gray-400'
                                    ]"
                                >
                                    <icon v-if="slideHasErrors[3]" icon="solar:danger-triangle-bold" class="tw-text-white tw-text-sm" />
                                    <span v-else>3</span>
                                </button>
                                <span :class="[
                                    'tw-text-xs tw-font-medium tw-text-center tw-hidden sm:tw-block',
                                    slideHasErrors[3]
                                        ? 'tw-text-red-500'
                                        : currentSlide >= 3 ? 'tw-text-primary-600' : currentSlide >= 2 ? 'tw-text-primary-600' : 'tw-text-gray-400'
                                ]">{{ $t('onboarding.progress.social') }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Slide Container with Transition -->
                    <div class="tw-flex-1 tw-relative tw-overflow-y-auto tw-overflow-x-hidden">
                        <transition
                            :name="slideDirection"
                            mode="out-in"
                            :duration="300"
                        >
                            <div :key="currentSlide" class="tw-h-full">
                                <!-- Slide 0: Language Selection -->
                                <OnboardingSlide0
                                    v-if="currentSlide === 0"
                                    :formData="formData"
                                    :errors="errors"
                                    @update:formData="updateFormData"
                                />

                                <!-- Slide 1: Basic Information -->
                                <OnboardingSlide1
                                    v-if="currentSlide === 1"
                                    :formData="formData"
                                    :errors="errors"
                                    @update:formData="updateFormData"
                                />

                                <!-- Slide 2: About & Address -->
                                <OnboardingSlide2
                                    v-if="currentSlide === 2"
                                    :formData="formData"
                                    :errors="errors"
                                    @update:formData="updateFormData"
                                />

                                <!-- Slide 3: Social Media -->
                                <OnboardingSlide3
                                    v-if="currentSlide === 3"
                                    :formData="formData"
                                    :errors="errors"
                                    @update:formData="updateFormData"
                                />
                            </div>
                        </transition>
                    </div>

                    <!-- Navigation Buttons -->
                    <div class="tw-flex tw-items-center tw-justify-between tw-pt-3 sm:tw-pt-4 tw-border-t tw-border-gray-200 tw-mt-3 sm:tw-mt-4 tw-flex-shrink-0">
                        <!-- Previous Button -->
                        <a-button
                            v-if="currentSlide > 0"
                            intent="outline"
                            left-icon="solar:arrow-left-linear"
                            @click="previousSlide"
                            :disabled="isSubmitting"
                            class="tw-text-xs sm:tw-text-sm tw-px-3 sm:tw-px-4 tw-py-2"
                        >
                            <span class="tw-hidden sm:tw-inline">{{ $t('onboarding.navigation.previous') }}</span>
                            <span class="tw-inline sm:tw-hidden">{{ $t('onboarding.navigation.back') }}</span>
                        </a-button>
                        <div v-else></div>

                        <!-- Next/Complete Button -->
                        <a-button
                            v-if="currentSlide < totalSlides - 1"
                            intent="primary"
                            right-icon="solar:arrow-right-linear"
                            @click="nextSlide"
                            :disabled="!canProceed || isSubmitting"
                            class="tw-text-xs sm:tw-text-sm tw-px-3 sm:tw-px-4 tw-py-2"
                        >
                            <span class="tw-hidden sm:tw-inline">{{ $t('onboarding.navigation.next') }}</span>
                            <span class="tw-inline sm:tw-hidden">{{ $t('onboarding.navigation.next') }}</span>
                        </a-button>
                        <a-button
                            v-else
                            intent="primary"
                            right-icon="solar:check-circle-bold"
                            @click="completeOnboarding"
                            :loading="isSubmitting"
                            :disabled="isSubmitting"
                            class="tw-text-xs sm:tw-text-sm tw-px-3 sm:tw-px-4 tw-py-2"
                        >
                            <span class="tw-hidden sm:tw-inline">{{ $t('onboarding.navigation.complete_setup') }}</span>
                            <span class="tw-inline sm:tw-hidden">{{ $t('onboarding.navigation.complete') }}</span>
                        </a-button>
                    </div>

                    <!-- Skip Option -->
                    <div class="tw-text-center tw-mt-2 tw-flex-shrink-0" v-if="currentSlide === 3">
                        <button
                            @click="completeOnboarding"
                            class="tw-text-xs tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors"
                            :disabled="isSubmitting"
                        >
                            {{ $t('onboarding.navigation.skip_social') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
/* eslint-disable */
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import AppLogo from '@/components/AppLogo.vue';
import brand from '@/config/brand';
import OnboardingSlide0 from '@/components/onboarding/OnboardingSlide0.vue';
import OnboardingSlide1 from '@/components/onboarding/OnboardingSlide1.vue';
import OnboardingSlide2 from '@/components/onboarding/OnboardingSlide2.vue';
import OnboardingSlide3 from '@/components/onboarding/OnboardingSlide3.vue';
import useAlert from '@/composables/useAlert';
import onboardingApi from '@/api/onboarding';
import { useAuthStore } from '@/stores';

const router = useRouter();
const { t } = useI18n();
const loading = ref(false);
const authStore = useAuthStore();
const user = authStore.user;

// Slide management
const currentSlide = ref(0);
const totalSlides = ref(4);
const slideDirection = ref('slide-next');
const isSubmitting = ref(false);

// Form data structure
const formData = reactive({
    // Language Selection (Slide 0)
    language: 'en',

    // Personal Information (Slide 1)
    full_name: '',
    profile_image: null,
    contact_email: '',

    // About & Address (Slide 2)
    about: '',
    address: {
        street: '',
        city: '',
        state: '',
        country_id: '',
        postal_code: ''
    },

    // Social Media (Slide 3)
    linkedin_url: '',
    instagram_url: '',
    twitter_url: '',
    facebook_url: '',
    youtube_url: '',
    github_url: '',
    website_url: ''
});

const fetchUserProfile = async () => {
    loading.value = true;
    await onboardingApi.get().then((res) => {
        console.log(res);
        Object.assign(formData, res.data.profile);
    }).catch((err) => {
        console.log(err);
    }).finally(() => {
        loading.value = false;
    });
    loading.value = false;
}

// Form update handler
const updateFormData = (newData) => {
    // Update each property individually to ensure reactivity
    Object.keys(newData).forEach(key => {
        if (formData.hasOwnProperty(key)) {
            formData[key] = newData[key];

            // Clear error for this field when content changes
            if (errors[key]) {
                delete errors[key];
            }

            // Handle address fields - API returns flat field names like 'postal_code'
            if (key === 'address' && typeof newData[key] === 'object') {
                Object.keys(newData[key]).forEach(addressKey => {
                    // Clear address field errors (API returns them as flat names)
                    if (errors[addressKey]) {
                        delete errors[addressKey];
                    }
                    // Also clear nested format just in case
                    if (errors[`address.${addressKey}`]) {
                        delete errors[`address.${addressKey}`];
                    }
                });
            }
        }
    });
};

// Form validation errors
const errors = reactive({});

// Error handling and slide navigation
const getSlideForField = (fieldName) => {
    // Map error field names to slide numbers
    const fieldToSlideMap = {
        // Slide 0 - Language
        'language': 0,

        // Slide 1 - Basic Information
        'full_name': 1,
        'profile_image': 1,
        'contact_email': 1,

        // Slide 2 - About & Address
        'about': 2,
        // API returns address errors as flat field names
        'street': 2,
        'city': 2,
        'state': 2,
        'country_id': 2,
        'postal_code': 2,
        // Support nested format just in case
        'address.street': 2,
        'address.city': 2,
        'address.state': 2,
        'address.country_id': 2,
        'address.postal_code': 2,

        // Slide 3 - Social Media
        'linkedin_url': 3,
        'instagram_url': 3,
        'twitter_url': 3,
        'facebook_url': 3,
        'youtube_url': 3,
        'github_url': 3,
        'website_url': 3
    };

    return fieldToSlideMap[fieldName] ?? null;
};

const slideHasErrors = computed(() => {
    const slideErrors = [false, false, false, false]; // Initialize for 4 slides

    Object.keys(errors).forEach(fieldName => {
        const slideNumber = getSlideForField(fieldName);
        if (slideNumber !== null && slideNumber >= 0 && slideNumber < 4) {
            slideErrors[slideNumber] = true;
        }
    });

    return slideErrors;
});

const getFirstSlideWithErrors = () => {
    const errorSlides = slideHasErrors.value;
    for (let i = 0; i < errorSlides.length; i++) {
        if (errorSlides[i]) {
            return i;
        }
    }
    return null;
};

const navigateToFirstError = () => {
    const firstErrorSlide = getFirstSlideWithErrors();
    if (firstErrorSlide !== null) {
        slideDirection.value = firstErrorSlide > currentSlide.value ? 'slide-next' : 'slide-prev';
        currentSlide.value = firstErrorSlide;
    }
};

const getFieldsForSlide = (slideNumber) => {
    // Return all field names that belong to a specific slide
    const slideFieldMap = {
        0: ['language'], // Language Selection
        1: ['full_name', 'profile_image', 'contact_email'], // Basic Information
        2: ['about', 'street', 'city', 'state', 'country_id', 'postal_code'], // About & Address
        3: ['linkedin_url', 'instagram_url', 'twitter_url', 'facebook_url', 'youtube_url', 'github_url', 'website_url'] // Social Media
    };

    return slideFieldMap[slideNumber] || [];
};

const clearSlideErrors = (slideNumber) => {
    const fieldsToRemove = getFieldsForSlide(slideNumber);
    fieldsToRemove.forEach(fieldName => {
        if (errors[fieldName]) {
            delete errors[fieldName];
        }
        // Also clear nested address format
        if (errors[`address.${fieldName}`]) {
            delete errors[`address.${fieldName}`];
        }
    });
};

// Computed properties
const canProceed = computed(() => {
    // Validation disabled for now
    return true;

    // if (currentSlide.value === 1) {
    //     // Required fields for slide 1
    //     return formData.full_name.trim() !== '' &&
    //            formData.contact_email.trim() !== '' &&
    //            isValidEmail(formData.contact_email);
    // }
    // // Slides 2 and 3 have no required fields
    // return true;
});

// Methods
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const validateCurrentSlide = () => {
    // Validation disabled for now
    return true;

    // // Clear previous errors
    // Object.keys(errors).forEach(key => {
    //     if (errors[key]) delete errors[key];
    // });

    // let isValid = true;

    // if (currentSlide.value === 1) {
    //     // Validate required fields
    //     if (!formData.full_name.trim()) {
    //         errors.full_name = 'Full name is required';
    //         isValid = false;
    //     }

    //     if (!formData.contact_email.trim()) {
    //         errors.contact_email = 'Email is required';
    //         isValid = false;
    //     } else if (!isValidEmail(formData.contact_email)) {
    //         errors.contact_email = 'Please enter a valid email address';
    //         isValid = false;
    //     }
    // }

    // if (currentSlide.value === 3) {
    //     // Validate URL formats for social media
    //     const urlFields = [
    //         'linkedin_url', 'instagram_url', 'twitter_url',
    //         'facebook_url', 'youtube_url', 'github_url', 'website_url'
    //     ];

    //     urlFields.forEach(field => {
    //         if (formData[field] && !isValidUrl(formData[field])) {
    //             errors[field] = 'Please enter a valid URL';
    //             isValid = false;
    //         }
    //     });
    // }

    // return isValid;
};

const isValidUrl = (string) => {
    try {
        new URL(string);
        return true;
    } catch (_) {
        return false;
    }
};

const nextSlide = () => {
    // Clear errors from current slide before moving forward
    clearSlideErrors(currentSlide.value);

    // Validate slide 2 (About & Address) before moving to slide 3 (Social Media)
    if (currentSlide.value === 2) {
        // Check if country_id is required and validate
        if (!formData.address.country_id || formData.address.country_id === '') {
            errors.country_id = t('errors.required', { field: t('onboarding.about_address.country') });
            return; // Prevent navigation
        }
    }

    // Allow navigation if validation passes or for other slides
    if (currentSlide.value < totalSlides.value) {
        slideDirection.value = 'slide-next';
        currentSlide.value += 1;
    }

    if(currentSlide.value === 1) {
        console.log('Language is selected')
    }

    if(currentSlide.value === 2) {
        console.log('informations done')
    }

    if(currentSlide.value === 3) {
        console.log('about done')
    }

    // if (validateCurrentSlide() && currentSlide.value < totalSlides.value) {
    //     slideDirection.value = 'slide-next';
    //     currentSlide.value += 1;
    // }


};

const previousSlide = () => {
    if (currentSlide.value > 1) {
        slideDirection.value = 'slide-prev';
        currentSlide.value -= 1;
    }
};

const goToSlide = (slideNumber) => {
    if (slideNumber === currentSlide.value) return;

    // Validation disabled for now - allow navigation to any slide
    slideDirection.value = slideNumber > currentSlide.value ? 'slide-next' : 'slide-prev';
    currentSlide.value = slideNumber;

    // // Only allow going to previous slides or next slide if current is valid
    // if (slideNumber < currentSlide.value ||
    //     (slideNumber === currentSlide.value + 1 && validateCurrentSlide())) {
    //     slideDirection.value = slideNumber > currentSlide.value ? 'slide-next' : 'slide-prev';
    //     currentSlide.value = slideNumber;
    // }
};

const completeOnboarding = async () => {
    // Validation disabled for now
    // if (!validateCurrentSlide()) {
    //     return;
    // }

    isSubmitting.value = true;

    try {
        let hasErrors = false;

        // Create FormData object for proper file upload handling
        const submitFormData = new FormData();

        // Add basic fields
        submitFormData.append('language', formData.language);
        submitFormData.append('full_name', formData.full_name);
        submitFormData.append('contact_email', formData.contact_email);
        submitFormData.append('about', formData.about);

        // Add profile image if exists
        if (formData.profile_image) {
            submitFormData.append('profile_image', formData.profile_image);
        }

        // Add address fields (flatten the nested object)
        if (formData.address) {
            Object.keys(formData.address).forEach(key => {
                if (formData.address[key]) {
                    submitFormData.append(key, formData.address[key]);
                }
            });
        }

        // Add social media URLs
        const socialFields = ['linkedin_url', 'instagram_url', 'twitter_url', 'facebook_url', 'youtube_url', 'github_url', 'website_url'];
        socialFields.forEach(field => {
            if (formData[field]) {
                submitFormData.append(field, formData[field]);
            }
        });

        console.log('Submitting onboarding data...');

        await onboardingApi.set(submitFormData).then((res) => {
            console.log('Onboarding success:', res);
        }).catch((err) => {
            if(err.response.data.errors) {
                Object.keys(err.response.data.errors).forEach(key => {
                    errors[key] = err.response.data.errors[key][0];
                });
                hasErrors = true;
            }
            console.log('Onboarding error:', err);
        }).finally(() => {
            isSubmitting.value = false;
        });

        if(hasErrors) {
            navigateToFirstError(); // Navigate to the first slide with errors
            return;
        }


        useAlert(`Onboarding completed successfully! Welcome to ${brand.name}!`, 'success');

        const toRoute = 'shared.dashboard';

        authStore.setUser({ ...user, is_completed: true });
        // Redirect to dashboard or home page
        router.push({ name: toRoute }); // Adjust route as needed

    } catch (error) {
        console.error('Error completing onboarding:', error);
        useAlert('There was an error completing your setup. Please try again.', 'error');
    } finally {
        isSubmitting.value = false;
    }
};

// Lifecycle
onMounted(() => {
    fetchUserProfile();
});
</script>

<style scoped>
/* Slide Transitions */
.slide-next-enter-active,
.slide-next-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
    transition: all 0.3s ease-in-out;
}

.slide-next-enter-from {
    opacity: 0;
    transform: translateX(30px);
}

.slide-next-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

.slide-prev-enter-from {
    opacity: 0;
    transform: translateX(-30px);
}

.slide-prev-leave-to {
    opacity: 0;
    transform: translateX(30px);
}
</style>
